import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ImagePlus, X, Sparkles } from "lucide-react";

export default function PostJob() {
  usePageMeta("Post a job", "Post your job for free and get custom quotes from vetted Black-owned businesses near you.");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState({ trades: [], service_areas: [] });
  const [saving, setSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [form, setForm] = useState({
    trade: params.get("trade") || "", city: params.get("city") || "", title: "",
    description: params.get("desc") || "",
    address: "", timing: "Flexible", budget_min: "", budget_max: "", photos: [],
  });
  const [step, setStep] = useState(params.get("desc") ? 3 : 1);

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPhotos = async (files) => {
    if (!files?.length) return;
    setUploadingPhotos(true);
    try {
      const ids = [];
      for (const f of Array.from(files)) {
        const fd = new FormData(); fd.append("file", f);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(up.data.file_id);
      }
      setForm((prev) => ({ ...prev, photos: [...prev.photos, ...ids] }));
    } catch { toast.error("Photo upload failed"); }
    finally { setUploadingPhotos(false); }
  };
  const removePhoto = (pid) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== pid) }));

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const aiAssist = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.post("/ai/job-assist", { prompt: aiPrompt });
      setForm((f) => ({ ...f, title: r.data.title || f.title, description: r.data.description || f.description,
        trade: r.data.trade || f.trade, budget_min: r.data.budget_min || f.budget_min, budget_max: r.data.budget_max || f.budget_max }));
      toast.success("Draft ready — review & edit below");
    } catch { toast.error("AI is busy, please try again"); }
    finally { setAiLoading(false); }
  };

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "client") { toast.error("Switch to a client account to post a job."); return; }
    setSaving(true);
    try {
      const r = await api.post("/jobs", {
        ...form,
        budget_min: parseFloat(form.budget_min) || 0,
        budget_max: parseFloat(form.budget_max) || 0,
      });
      toast.success("Job posted! Vetted businesses in your area have been notified.");
      navigate(`/jobs/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post job");
    } finally { setSaving(false); }
  };

  const canNext = () => {
    if (step === 1) return form.trade;
    if (step === 2) return form.city;
    if (step === 3) return form.title && form.description;
    return true;
  };

  const steps = ["Trade", "Location", "Details", "Budget"];

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-heading text-3xl font-extrabold text-slate-950">Post a job</h1>
      <div className="mt-6 flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1 last:flex-none">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${i + 1 < step ? "bg-emerald-500 text-white" : i + 1 === step ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-400"}`}>
              {i + 1 < step ? <Check size={14} /> : i + 1}
            </div>
            <span className={`text-xs font-medium ${i + 1 === step ? "text-amber-700" : "text-slate-400"}`}>{s}</span>
            {i < steps.length - 1 && <div className={`h-0.5 flex-1 ${i + 1 < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-5">
        {step === 1 && (
          <div>
            <label className="text-sm font-semibold text-slate-700">What trade or service do you need?</label>
            <Select value={form.trade} onValueChange={(v) => set("trade", v)}>
              <SelectTrigger data-testid="job-trade-select" className="mt-2 h-11"><SelectValue placeholder="Select a trade" /></SelectTrigger>
              <SelectContent>{meta.trades.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {step === 2 && (
          <>
            <div>
              <label className="text-sm font-semibold text-slate-700">Service area</label>
              <Select value={form.city} onValueChange={(v) => set("city", v)}>
                <SelectTrigger data-testid="job-city-select" className="mt-2 h-11"><SelectValue placeholder="Select your city" /></SelectTrigger>
                <SelectContent>{meta.service_areas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Address (optional)</label>
              <Input data-testid="job-address-input" className="mt-2 h-11" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Timing</label>
              <Select value={form.timing} onValueChange={(v) => set("timing", v)}>
                <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Urgent (ASAP)", "Within a week", "Within a month", "Flexible"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <label className="text-xs font-semibold text-amber-800 flex items-center gap-1"><Sparkles size={13} /> Describe it in a sentence — AI drafts the rest</label>
              <div className="mt-2 flex gap-2">
                <Input data-testid="ai-job-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g. leaky kitchen faucet and a slow drain" />
                <Button type="button" data-testid="ai-job-assist-btn" onClick={aiAssist} disabled={aiLoading} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full whitespace-nowrap">{aiLoading ? "Thinking…" : "✨ Draft"}</Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Job title</label>
              <Input data-testid="job-title-input" className="mt-2 h-11" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Replace kitchen faucet & fix leak" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Describe the work</label>
              <Textarea data-testid="job-description-input" className="mt-2 min-h-[140px]" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Give details so businesses can quote accurately…" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Photos (optional)</label>
              {user ? (
                <div className="mt-2">
                  <div className="flex flex-wrap gap-2">
                    {form.photos.map((pid) => (
                      <div key={pid} className="relative">
                        <img src={fileUrl(pid)} alt="job" className="h-20 w-20 object-cover rounded-lg border border-slate-200" />
                        <button type="button" data-testid={`remove-photo-${pid}`} onClick={() => removePhoto(pid)}
                          className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"><X size={12} /></button>
                      </div>
                    ))}
                    <label className="cursor-pointer h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-amber-400">
                      <ImagePlus size={18} />
                      <span className="text-[10px] mt-0.5">{uploadingPhotos ? "…" : "Add"}</span>
                      <input type="file" accept="image/*" multiple className="hidden" data-testid="job-photos-input" onChange={(e) => uploadPhotos(e.target.files)} />
                    </label>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-slate-400">Sign in to attach photos (you can still post after signing in).</p>
              )}
            </div>
          </>
        )}
        {step === 4 && (
          <>
            <p className="text-sm text-slate-500">Custom quotes only — set an optional budget range in CAD so businesses know your ballpark.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Budget min ($)</label>
                <Input data-testid="job-budget-min-input" type="number" className="mt-2 h-11" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} placeholder="500" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Budget max ($)</label>
                <Input data-testid="job-budget-max-input" type="number" className="mt-2 h-11" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} placeholder="2000" />
              </div>
            </div>
          </>
        )}

        <div className="flex items-center justify-between pt-4">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          {step < 4 ? (
            <Button data-testid="job-post-step-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)}
              className="bg-slate-950 hover:bg-amber-600 text-white rounded-full px-6">
              Next <ArrowRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button data-testid="job-post-submit" disabled={saving} onClick={submit}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-6">
              {saving ? "Posting…" : "Post job"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
